import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import toast from 'react-hot-toast';
import { spacing } from '../theme';
import Card from '../components/Card';
import Button from '../components/Button';
import IncomingQueue from './IncomingQueue';
import PeopleAccounts from './PeopleAccounts';
import AuditLog from './AuditLog';
import MatchingAccountProfile from './MatchingAccountProfile';
import { fetchUnmatchedCount } from './hooks/useIncomingRecords';
import { useMatching } from './hooks/useMatching';

function NwraImportPanel({ onImported }) {
  const [jsonText, setJsonText] = useState('');
  const [busy, setBusy] = useState(false);
  const { importNwraRecords, runWithToast } = useMatching();

  const handleImport = async () => {
    try {
      const parsed = JSON.parse(jsonText);
      const records = Array.isArray(parsed) ? parsed : [parsed];
      setBusy(true);
      await runWithToast(`Imported ${records.length} NWRA record(s)`, () => importNwraRecords(records));
      setJsonText('');
      onImported?.();
    } catch (err) {
      toast.error(err.message || 'Invalid JSON');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Manual NWRA import" style={{ marginBottom: spacing.lg }}>
      <p style={{ fontSize: '0.9rem', marginBottom: spacing.sm }}>
        Paste JSON array of NWRA records (email, company_id, type, standing, etc.) to populate the incoming queue.
      </p>
      <textarea
        value={jsonText}
        onChange={(e) => setJsonText(e.target.value)}
        rows={6}
        style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8rem', padding: 8, boxSizing: 'border-box' }}
        placeholder='[{"external_id":"co_123","email":"user@example.com","type":"status_update","standing":"good"}]'
      />
      <Button variant="primary" size="sm" loading={busy} onClick={handleImport} style={{ marginTop: spacing.sm }}>
        Import records
      </Button>
    </Card>
  );
}

function MatchingPortal() {
  const [reloadToken, setReloadToken] = useState(0);
  const [unmatchedCount, setUnmatchedCount] = useState(0);

  useEffect(() => {
    fetchUnmatchedCount().then(setUnmatchedCount);
  }, [reloadToken]);

  const bump = () => setReloadToken((t) => t + 1);

  return (
    <Routes>
      <Route path="/" element={<Navigate to="incoming" replace />} />
      <Route
        path="incoming"
        element={
          <IncomingQueue
            title={`Incoming / All${unmatchedCount ? ` (${unmatchedCount} open)` : ''}`}
            reloadToken={reloadToken}
            onSelectRecord={() => fetchUnmatchedCount().then(setUnmatchedCount)}
          />
        }
      />
      <Route
        path="incoming/nwra"
        element={
          <>
            <NwraImportPanel onImported={bump} />
            <IncomingQueue sourceFilter="nwra" title="Incoming NWRA" reloadToken={reloadToken} />
          </>
        }
      />
      <Route
        path="incoming/stripe"
        element={<IncomingQueue sourceFilter="stripe" title="Incoming Stripe" reloadToken={reloadToken} />}
      />
      <Route
        path="unmatched"
        element={
          <IncomingQueue
            statusFilter="unmatched"
            title={`Unmatched${unmatchedCount ? ` (${unmatchedCount})` : ''}`}
            reloadToken={reloadToken}
          />
        }
      />
      <Route
        path="matched"
        element={<IncomingQueue statusFilter="matched" title="Matched / Linked" reloadToken={reloadToken} />}
      />
      <Route path="people" element={<PeopleAccounts />} />
      <Route path="accounts/:clientId" element={<MatchingAccountProfile />} />
      <Route path="audit" element={<AuditLog />} />
      <Route path="*" element={<Navigate to="incoming" replace />} />
    </Routes>
  );
}

export default MatchingPortal;
